import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import {
  teamService,
  type Organization,
  type TeamMember,
} from '../../services'
import {
  Button,
  Input,
  Modal,
  LoadingScreen,
  UsageCard,
  useToast,
} from '../../components/common'
import type { UserRole } from '../../types/database.types'

export function TeamPage() {
  const { profile } = useAuthStore()
  const toast = useToast()

  const [isLoading, setIsLoading] = useState(true)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [seatUsage, setSeatUsage] = useState({ current: 0, max: 1 })

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState<TeamMember | null>(null)

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<UserRole>('provider')
  const [isInviting, setIsInviting] = useState(false)

  const hasCorporateTier =
    profile?.subscription_tier === 'corporate' ||
    profile?.subscription_tier === 'platinum'

  const isOwner = organization?.owner_id === profile?.id
  const isAdmin = members.find(m => m.user_id === profile?.id)?.role === 'org_admin' || isOwner

  useEffect(() => {
    loadTeamData()
  }, [profile])

  const loadTeamData = async () => {
    if (!profile || !hasCorporateTier) {
      setIsLoading(false)
      return
    }

    try {
      // Get or create organization
      const org = await teamService.getOrCreateOrganization(
        profile.id,
        profile.full_name || profile.email
      )

      if (org) {
        setOrganization(org)

        // Load members and seat usage
        const [teamMembers, usage] = await Promise.all([
          teamService.getTeamMembers(org.id),
          teamService.getSeatUsage(org.id),
        ])

        setMembers(teamMembers)
        setSeatUsage(usage)
      }
    } catch (error) {
      console.error('Failed to load team data:', error)
      toast.error('Failed to load team data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInvite = async () => {
    if (!organization || !inviteEmail.trim()) return

    // Check seat limit
    if (seatUsage.current >= seatUsage.max) {
      toast.error('You have reached your seat limit. Upgrade to add more members.')
      return
    }

    setIsInviting(true)
    try {
      const result = await teamService.inviteTeamMember({
        email: inviteEmail.trim(),
        role: inviteRole,
        organizationId: organization.id,
      })

      if (result.success) {
        toast.success(result.message)
        setShowInviteModal(false)
        setInviteEmail('')
        setInviteRole('provider')
        loadTeamData() // Refresh member list
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      toast.error('Failed to send invitation')
    } finally {
      setIsInviting(false)
    }
  }

  const handleRemoveMember = async () => {
    if (!showRemoveModal) return

    try {
      const success = await teamService.removeMember(showRemoveModal.id)
      if (success) {
        toast.success('Team member removed')
        setShowRemoveModal(null)
        loadTeamData()
      } else {
        toast.error('Failed to remove team member')
      }
    } catch (error) {
      toast.error('Failed to remove team member')
    }
  }

  const handleRoleChange = async (member: TeamMember, newRole: UserRole) => {
    try {
      const success = await teamService.updateMemberRole(member.id, newRole)
      if (success) {
        toast.success('Role updated')
        loadTeamData()
      } else {
        toast.error('Failed to update role')
      }
    } catch (error) {
      toast.error('Failed to update role')
    }
  }

  if (!hasCorporateTier) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Team Management Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Upgrade to Corporate or Platinum to manage team members and share your video library.
        </p>
        <Link to="/dashboard/subscription">
          <Button>Upgrade to Corporate</Button>
        </Link>
      </div>
    )
  }

  if (isLoading) {
    return <LoadingScreen message="Loading team..." />
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="mt-1 text-gray-600">
            Manage your team members and their access to your organization.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setShowInviteModal(true)}
            disabled={seatUsage.current >= seatUsage.max}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Invite Member
          </Button>
        )}
      </div>

      {/* Seat Usage */}
      <div className="grid md:grid-cols-2 gap-6">
        <UsageCard
          label="Team Seats"
          current={seatUsage.current}
          max={seatUsage.max}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500">Organization</p>
              <p className="font-semibold text-gray-900">{organization?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Team Members ({members.length})</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-4 p-4 hover:bg-gray-50"
            >
              {/* Avatar */}
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                {member.user.avatar_url ? (
                  <img
                    src={member.user.avatar_url}
                    alt={member.user.full_name || 'Member'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-blue-600 font-medium">
                    {member.user.full_name?.[0]?.toUpperCase() ||
                      member.user.email[0].toUpperCase()}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {member.user.full_name || member.user.email}
                  {member.user_id === profile?.id && (
                    <span className="ml-2 text-sm text-gray-500">(You)</span>
                  )}
                </p>
                <p className="text-sm text-gray-500 truncate">{member.user.email}</p>
              </div>

              {/* Role Badge / Selector */}
              <div className="flex items-center gap-2">
                {organization?.owner_id === member.user_id ? (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                    Owner
                  </span>
                ) : isAdmin && member.user_id !== profile?.id ? (
                  <select
                    value={member.role}
                    onChange={(e) => handleRoleChange(member, e.target.value as UserRole)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 bg-white"
                  >
                    <option value="provider">Provider</option>
                    <option value="org_admin">Admin</option>
                  </select>
                ) : (
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    member.role === 'org_admin'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {member.role === 'org_admin' ? 'Admin' : 'Provider'}
                  </span>
                )}
              </div>

              {/* Remove Button */}
              {isAdmin &&
                member.user_id !== profile?.id &&
                organization?.owner_id !== member.user_id && (
                  <button
                    onClick={() => setShowRemoveModal(member)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove member"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
            </div>
          ))}

          {members.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p>No team members yet.</p>
              <p className="text-sm mt-1">Invite members to start collaborating.</p>
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite Team Member"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="provider">Provider - Can manage their own content</option>
              <option value="org_admin">Admin - Can manage team and all content</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={!inviteEmail.trim() || isInviting}
            >
              {isInviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={!!showRemoveModal}
        onClose={() => setShowRemoveModal(null)}
        title="Remove Team Member"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to remove{' '}
            <span className="font-medium text-gray-900">
              {showRemoveModal?.user.full_name || showRemoveModal?.user.email}
            </span>{' '}
            from your team? They will lose access to your organization's content.
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowRemoveModal(null)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleRemoveMember}
              className="!border-red-300 !text-red-600 hover:!bg-red-50"
            >
              Remove Member
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
